import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createApi } from "util/ApiCreator";
import { ProcessExecutionResponse } from "api/main/data-contracts";
import { ProcessExecutionAdminController } from "api/main/ProcessExecutionAdminController";
import { useTestMode } from "context/TestModeContext";
import { mockProcessExecutions } from "data/mockData";
import { adminTheme, statusColors, getStatusLabel } from "theme/adminTheme";
import {
  Box,
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FilterListIcon from "@mui/icons-material/FilterList";
import GavelIcon from "@mui/icons-material/Gavel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ErrorIcon from "@mui/icons-material/Error";
import RefreshIcon from "@mui/icons-material/Refresh";

export interface processExecutionAdminData extends ProcessExecutionResponse {
  email?: string;
}


function getStatusIcon(status: string) {
  switch (status) {
    case "IN_PROGRESS":
      return <ScheduleIcon sx={{ fontSize: 18 }} />;
    case "FINISHED":
    case "DEAL_DONE":
      return <CheckCircleIcon sx={{ fontSize: 18 }} />;
    case "CANCELLED":
      return <ErrorIcon sx={{ fontSize: 18 }} />;
    default:
      return <ScheduleIcon sx={{ fontSize: 18 }} />;
  }
}

function getStatusColor(status: string) {
  switch (status) {
    case "IN_PROGRESS":
      return statusColors.in_progress;
    case "FINISHED":
    case "DEAL_DONE":
      return statusColors.finished;
    case "CANCELLED":
      return statusColors.cancelled;
    default:
      return statusColors.pending;
  }
}

export default function ProcessExecutionAdminList() {
  const navigate = useNavigate();
  const { isTestMode } = useTestMode();
  const executionApi = useRef(createApi("processexecutionadmin") as ProcessExecutionAdminController);

  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [selectedStepStatus, setSelectedStepStatus] = useState<string>("");
  const [statuses, setStatuses] = useState<string[]>([]);
  const [stepStatuses, setStepStatuses] = useState<string[]>([]);
  const [executions, setExecutions] = useState<ProcessExecutionResponse[]>([]);
  const [filteredExecutions, setFilteredExecutions] = useState<ProcessExecutionResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (isTestMode) {
      // Sort by ID descending (newest first)
      const sorted = [...mockProcessExecutions].sort((a, b) => (b.id || 0) - (a.id || 0));
      setExecutions(sorted);
      setFilteredExecutions(sorted);
      setLoading(false);
    } else {
      getData();
    }
  }, [isTestMode]);

  useEffect(() => {
    setAllStatuses();
    getAllStepStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executions]);

  useEffect(() => {
    let filteredData = [...executions];

    if (selectedStatus) {
      filteredData = filteredData.filter((e) => e.status === selectedStatus);
    }

    if (selectedStepStatus) {
      filteredData = filteredData.filter((e) => {
        const stepExecutions = e.stepExecutions || [];
        return stepExecutions.find(
          (step) => step.name === selectedStepStatus && step.status === "ACTIVE"
        );
      });
    }

    setFilteredExecutions(filteredData);
  }, [selectedStatus, selectedStepStatus, executions]);

  async function getData() {
    setLoading(true);
    try {
      const data = await executionApi.current.findAll();
      // Sort by ID descending (newest first)
      const sorted = (data || []).sort((a: ProcessExecutionResponse, b: ProcessExecutionResponse) => (b.id || 0) - (a.id || 0));
      setExecutions(sorted);
      setFilteredExecutions(sorted);
    } catch (error) {
      console.error("Error fetching executions:", error);
    } finally {
      setLoading(false);
    }
  }

  function setAllStatuses() {
    const uniqueStatuses: string[] = [];
    executions.forEach((execution) => {
      if (execution.status && !uniqueStatuses.includes(execution.status)) {
        uniqueStatuses.push(execution.status);
      }
    });
    setStatuses(uniqueStatuses);
  }

  function getAllStepStatuses() {
    const uniqueStatuses: string[] = [];
    executions.forEach((execution) => {
      execution.stepExecutions?.forEach((step) => {
        if (step.name && !uniqueStatuses.includes(step.name)) {
          uniqueStatuses.push(step.name);
        }
      });
    });
    setStepStatuses(uniqueStatuses);
  }

  const clearFilters = () => {
    setSelectedStatus("");
    setSelectedStepStatus("");
  };

  return (
    <Box sx={{ bgcolor: adminTheme.colors.backgroundAlt, minHeight: "100vh" }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                color: adminTheme.colors.textPrimary,
              }}
            >
              Visi procesai
            </Typography>
            <Typography
              variant="body"
              sx={{ color: adminTheme.colors.textSecondary }}
            >
              Valdykite ir stebėkite procesų vykdymus
            </Typography>
          </Box>
          <Tooltip title="Atnaujinti">
            <IconButton
              onClick={() => (isTestMode ? null : getData())}
              sx={{
                bgcolor: adminTheme.colors.background,
                border: `1px solid ${adminTheme.colors.border}`,
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {loading && <LinearProgress sx={{ mb: 3 }} />}

        {/* Stats */}
        <Box sx={{ display: "flex", gap: 3, mb: 4, flexWrap: "wrap" }}>
          <Card
            sx={{
              flex: 1,
              minWidth: 180,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Iš viso vykdymų
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.textPrimary }}
            >
              {executions.length}
            </Typography>
          </Card>
          <Card
            sx={{
              flex: 1,
              minWidth: 180,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Vykdomi
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.primary }}
            >
              {executions.filter((e) => e.status === "IN_PROGRESS").length}
            </Typography>
          </Card>
          <Card
            sx={{
              flex: 1,
              minWidth: 180,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Užbaigti
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.success }}
            >
              {executions.filter((e) => e.status === "FINISHED").length}
            </Typography>
          </Card>
          <Card
            sx={{
              flex: 1,
              minWidth: 180,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Atšaukti
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.error }}
            >
              {executions.filter((e) => e.status === "CANCELLED").length}
            </Typography>
          </Card>
        </Box>

        {/* Filters */}
        <Card
          sx={{
            p: 3,
            mb: 3,
            borderRadius: adminTheme.borderRadius.lg,
            border: `1px solid ${adminTheme.colors.border}`,
            boxShadow: "none",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <FilterListIcon sx={{ color: adminTheme.colors.textSecondary }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Filtrai
            </Typography>
            {(selectedStatus || selectedStepStatus) && (
              <Button size="small" onClick={clearFilters}>
                Išvalyti
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <FormControl sx={{ minWidth: 250 }} size="small">
              <InputLabel>Proceso statusas</InputLabel>
              <Select
                value={selectedStatus}
                label="Proceso statusas"
                onChange={(e) => setSelectedStatus(e.target.value)}
                sx={{ borderRadius: adminTheme.borderRadius.sm }}
              >
                <MenuItem value="">Visi</MenuItem>
                {statuses.map((item) => (
                  <MenuItem key={item} value={item}>
                    {getStatusLabel(item)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 250 }} size="small">
              <InputLabel>Aktyvus žingsnis</InputLabel>
              <Select
                value={selectedStepStatus}
                label="Aktyvus žingsnis"
                onChange={(e) => setSelectedStepStatus(e.target.value)}
                sx={{ borderRadius: adminTheme.borderRadius.sm }}
              >
                <MenuItem value="">Visi</MenuItem>
                {stepStatuses.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Card>

        {/* Table */}
        <Card
          sx={{
            borderRadius: adminTheme.borderRadius.lg,
            border: `1px solid ${adminTheme.colors.border}`,
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: adminTheme.colors.backgroundAlt,
                    "& th": {
                      fontWeight: 600,
                      color: adminTheme.colors.textPrimary,
                      borderBottom: `2px solid ${adminTheme.colors.border}`,
                      py: 2,
                    },
                  }}
                >
                  <TableCell>ID</TableCell>
                  <TableCell>Proceso pavadinimas</TableCell>
                  <TableCell>Vartotojas</TableCell>
                  <TableCell>Aktyvus žingsnis</TableCell>
                  <TableCell>Statusas</TableCell>
                  <TableCell align="right">Veiksmai</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredExecutions.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 6,
                          color: adminTheme.colors.textMuted,
                        }}
                      >
                        <GavelIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                        <Typography>Nėra vykdymų</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredExecutions.map((execution) => {
                    const activeStep = execution.stepExecutions?.find(
                      (s) => s.status === "ACTIVE"
                    );
                    return (
                      <TableRow
                        key={execution.id}
                        sx={{
                          cursor: "pointer",
                          transition: adminTheme.transitions.fast,
                          "&:hover": {
                            bgcolor: "rgba(99, 102, 241, 0.04)",
                          },
                        }}
                        onClick={() =>
                          navigate(`/process/execution/${execution.id}`)
                        }
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: adminTheme.colors.primary,
                            }}
                          >
                            #{execution.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{ display: "flex", alignItems: "center", gap: 2 }}
                          >
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: "50%",
                                bgcolor: getStatusColor(execution.status || "").bg,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: getStatusColor(execution.status || "").text,
                              }}
                            >
                              {getStatusIcon(execution.status || "")}
                            </Box>
                            <Typography sx={{ fontWeight: 500 }}>
                              {execution.process?.name || "N/A"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {execution.accountInfoResponse?.email || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {activeStep?.name || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(execution.status)}
                            size="small"
                            sx={{
                              bgcolor: getStatusColor(execution.status || "").bg,
                              color: getStatusColor(execution.status || "").text,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Peržiūrėti">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/process/execution/${execution.id}`);
                              }}
                            >
                              <VisibilityIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>
    </Box>
  );
}
